import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useRescheduleEvent, usePatchEventTask } from "@/api/hooks/useEvents";
import type { Event, EventTask } from "@/api/types/event";
import { isTaskOverdue, sortTasksByDueDate, formatShortDate } from "./eventDate";

interface TaskListCardProps {
  event: Event;
}

/**
 * The T-minus task engine's UI: dated tasks (overdue ones in red), a
 * done/waive/reopen toggle per task, inline notes, and the reschedule
 * control that re-dates every open task via POST /reschedule.
 */
export function TaskListCard({ event }: TaskListCardProps) {
  const tasks = sortTasksByDueDate(event.tasks ?? []);
  const [rescheduleDate, setRescheduleDate] = useState(event.event_date);
  const rescheduleEvent = useRescheduleEvent();

  async function handleReschedule() {
    if (!rescheduleDate || rescheduleDate === event.event_date) return;
    try {
      await rescheduleEvent.mutateAsync({ id: event.id, event_date: rescheduleDate });
      toastSuccess("Event rescheduled — tasks re-dated");
    } catch (err) {
      toastError("Couldn't reschedule", getErrorMessage(err));
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>T-minus tasks</CardTitle>
        <div className="flex items-center gap-2">
          <label htmlFor="reschedule-date" className="text-xs font-medium text-text-secondary">
            Reschedule to
          </label>
          <input
            id="reschedule-date"
            type="date"
            value={rescheduleDate}
            onChange={(e) => setRescheduleDate(e.target.value)}
            className="h-9 rounded-md border border-border bg-bg-card px-2 text-sm"
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void handleReschedule()}
            disabled={rescheduleEvent.isPending || rescheduleDate === event.event_date}
          >
            {rescheduleEvent.isPending ? "Saving..." : "Reschedule"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <p className="text-sm text-text-muted">No tasks on this event.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="task-list">
            {tasks.map((task) => (
              <TaskRow key={task.id} eventId={event.id} task={task} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface TaskRowProps {
  eventId: string;
  task: EventTask;
}

function TaskRow({ eventId, task }: TaskRowProps) {
  const patchTask = usePatchEventTask();
  const [notes, setNotes] = useState(task.notes ?? "");
  const overdue = isTaskOverdue(task);

  async function setStatus(status: EventTask["status"]) {
    try {
      await patchTask.mutateAsync({ eventId, taskId: task.id, status });
    } catch (err) {
      toastError("Couldn't update task", getErrorMessage(err));
    }
  }

  async function saveNotes() {
    if (notes === (task.notes ?? "")) return;
    try {
      await patchTask.mutateAsync({ eventId, taskId: task.id, notes });
    } catch (err) {
      toastError("Couldn't save notes", getErrorMessage(err));
    }
  }

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-task-id={task.id}>
      <div className="flex items-center gap-3">
        <span
          className={overdue ? "text-sm font-semibold text-danger" : "text-sm text-text-secondary"}
          data-testid={`task-due-${task.id}`}
        >
          {formatShortDate(task.due_date)}
          {overdue ? " (overdue)" : ""}
        </span>
        <span
          className={
            task.status === "done"
              ? "text-sm text-text-muted line-through"
              : "text-sm text-text-primary"
          }
        >
          {task.title}
        </span>
        {task.status !== "open" && (
          <Badge variant={task.status === "done" ? "success" : "secondary"} size="sm">
            {task.status === "done" ? "Done" : "Waived"}
          </Badge>
        )}
      </div>

      <div className="flex flex-1 items-center gap-2 sm:justify-end">
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => void saveNotes()}
          placeholder="Notes"
          className="h-8 w-40 rounded-md border border-border bg-bg-card px-2 text-xs"
          aria-label={`Notes for ${task.title}`}
        />
        {task.status === "open" ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => void setStatus("done")}>
              Done
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void setStatus("waived")}>
              Waive
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => void setStatus("open")}>
            Reopen
          </Button>
        )}
      </div>
    </li>
  );
}
